import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  getEventInstance,
  getNextOpenMicInstance,
  getOpenMicSignups,
  getPastOpenMicInstances,
  getUpcomingOpenMicInstances,
  removePerformer,
  sendPerformerEmails,
  updatePerformerStatus,
} from "../../services/firebase";

import EmailConfirmationModal from "./EmailConfirmationModal";
import SocialMediaCell from "./SocialMediaCell";

const OpenMicAdmin = () => {
  const [signups, setSignups] = useState([]);
  const [eventInstances, setEventInstances] = useState([]);
  const [selectedEventInstance, setSelectedEventInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Helper function to format dates
  const formatDate = (date) => {
    return date instanceof Date
      ? date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          weekday: "short",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "Invalid date";
  };

  // Check if an event is in the past
  const isEventInPast = (event) => {
    if (!event || !event.startDate) return false;
    const now = new Date();
    return event.startDate < now;
  };

  // Fetch event instances and signups
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Get past and upcoming instances
        const pastInstances = await getPastOpenMicInstances(3);
        const upcomingInstances = await getUpcomingOpenMicInstances(3);

        // Combine and sort them
        const allInstances = [...pastInstances, ...upcomingInstances];
        allInstances.sort((a, b) => a.startDate - b.startDate);

        setEventInstances(allInstances);

        // Select the next upcoming event by default
        const nextEvent = await getNextOpenMicInstance();
        if (nextEvent) {
          setSelectedEventInstance(nextEvent);

          // Fetch signups for this event
          const data = await getOpenMicSignups(nextEvent.id);
          data.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
          setSignups(data);
        } else if (allInstances.length > 0) {
          // If no upcoming event, select the most recent one
          setSelectedEventInstance(allInstances[allInstances.length - 1]);

          // Fetch signups for this event
          const data = await getOpenMicSignups(
            allInstances[allInstances.length - 1].id
          );
          data.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
          setSignups(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbar({
          open: true,
          message: "Failed to load data",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle event instance change
  const handleEventInstanceChange = async (event) => {
    const instanceId = event.target.value;

    if (!instanceId) return;

    try {
      setIsLoading(true);

      // Get the selected event instance
      const instance = await getEventInstance(instanceId);
      setSelectedEventInstance(instance);

      // Fetch signups for this event
      const data = await getOpenMicSignups(instanceId);
      data.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
      setSignups(data);
    } catch (error) {
      console.error("Error fetching signups:", error);
      setSnackbar({
        open: true,
        message: "Failed to load signups",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id, selected) => {
    try {
      await updatePerformerStatus(id, selected);
      setSignups(
        signups.map((signup) =>
          signup.id === id ? { ...signup, selected } : signup
        )
      );
    } catch (error) {
      console.error("Error updating performer status:", error);
      setSnackbar({
        open: true,
        message: "Failed to update performer status",
        severity: "error",
      });
    }
  };

  const handleRemove = async (id) => {
    if (window.confirm("Are you sure you want to remove this performer?")) {
      try {
        await removePerformer(id);
        setSignups(signups.filter((signup) => signup.id !== id));
      } catch (error) {
        console.error("Error removing performer:", error);
        setSnackbar({
          open: true,
          message: "Failed to remove performer",
          severity: "error",
        });
      }
    }
  };

  const handleSendEmails = async (emailData) => {
    try {
      const emailsSent = await sendPerformerEmails(
        emailData,
        selectedEventInstance
      );
      console.log({ emailsSent });
      setSnackbar({
        open: true,
        message: "Emails sent successfully",
        severity: "success",
      });
      setIsEmailModalOpen(false);
    } catch (error) {
      console.error("Error sending emails:", error);
      setSnackbar({
        open: true,
        message: "Failed to send emails",
        severity: "error",
      });
    }
  };

  const selectedPerformers = signups.filter((signup) => signup.selected);
  const isPastEvent = isEventInPast(selectedEventInstance);

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4">Open Mic Admin</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsEmailModalOpen(true)}
          disabled={selectedPerformers.length === 0 || isPastEvent}
        >
          Send Emails to Performers ({selectedPerformers.length})
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel id="event-instance-select-label">Event Date</InputLabel>
          <Select
            labelId="event-instance-select-label"
            id="event-instance-select"
            value={selectedEventInstance?.id || ""}
            label="Event Date"
            onChange={handleEventInstanceChange}
            disabled={isLoading}
          >
            {eventInstances.map((instance) => (
              <MenuItem
                key={instance.id}
                value={instance.id}
                sx={{
                  fontWeight:
                    instance.id === selectedEventInstance?.id
                      ? "bold"
                      : "normal",
                  color: isEventInPast(instance)
                    ? "text.secondary"
                    : "text.primary",
                }}
              >
                {formatDate(instance.startDate)}{" "}
                {isEventInPast(instance) ? "(Past)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isPastEvent && (
        <Alert severity="info" sx={{ mb: 4 }}>
          This is a past event. You can view the list but cannot make changes.
        </Alert>
      )}

      <EmailConfirmationModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        selectedPerformers={selectedPerformers}
        eventInstance={selectedEventInstance}
        onRemovePerformer={(id) => handleStatusChange(id, false)}
        onSendEmails={handleSendEmails}
      />

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      ) : signups.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6">No signups for this event yet</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Stage Name</TableCell>
                <TableCell>Email & Social</TableCell>
                <TableCell>Sign Up Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signups.map((signup, index) => (
                <TableRow key={signup.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{signup.name}</TableCell>
                  <TableCell>{signup.stageName}</TableCell>
                  <TableCell>
                    <SocialMediaCell signup={signup} />
                  </TableCell>
                  <TableCell>
                    {signup.createdAt.toDate().toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      fullWidth
                      variant={signup.selected ? "contained" : "outlined"}
                      onClick={() =>
                        handleStatusChange(signup.id, !signup.selected)
                      }
                      disabled={isPastEvent}
                    >
                      {signup.selected ? "✅ Selected" : "Select"}
                    </Button>
                    <Box sx={{ my: 1 }} />
                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      onClick={() => handleRemove(signup.id)}
                      disabled={isPastEvent}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default OpenMicAdmin;
